import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './MainPage.module.css';
import GlobalHeader from '../GlobalHeader';
import ParallaxHero from './ParallaxHero';
import AboutModal from '../AboutModal/AboutModal';
import AuthModal from '../AuthModal/AuthModal';
import UserProfileModal from '../UserProfileModal/UserProfileModal';
import PremiumModal from '../PremiumModal/PremiumModal';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import DonationModal from '../DonationModal/DonationModal';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import { AuthContext } from '../Providers/AuthContext';
import { useLanguage } from '../Providers/LanguageContext';
import { PieceType } from '../../services/zodiacEngine/types';
import { UserDataService } from '../../services/firebase/userDataService';
import { MMRService } from '../../services/MMR/MMRService';
import { usePlayersOnline } from '../../hooks/usePlayersOnline';
import { playUISound, preloadUISound } from '../../utils/UISoundEffects';

// Landing-icon carousel source: 13 pieces × {light, dark} = 26 variants
type CarouselItem = { name: string; variant: 'light' | 'dark'; file: string };
const LANDING_PIECE_NAMES = [
  'Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus',
  'Penguin', 'Rhino', 'Snake', 'Tree', 'Virus', 'Wolf',
] as const;
const LANDING_PIECE_ICONS: CarouselItem[] = LANDING_PIECE_NAMES.flatMap(name => [
  { name, variant: 'light' as const, file: `${name}-light.png` },
  { name, variant: 'dark'  as const, file: `${name}-dark.png` },
]);

// Fisher-Yates shuffle, returns a new array
function shuffleArray<T>(input: readonly T[]): T[] {
  const out = input.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div className={styles.faqItem}>
    <button
      className={styles.faqQuestion}
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span>{question}</span>
      <span className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`}>▼</span>
    </button>
    <div className={`${styles.faqAnswer} ${isOpen ? styles.faqAnswerOpen : ''}`}>
      <div className={styles.faqAnswerContent}>
        {answer}
      </div>
    </div>
  </div>
);

function MainPage() {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  // The hover/carousel tick now lives in src/utils/UISoundEffects so the back
  // arrows and PLAY NOW can play the same one (see playUISound).
  useEffect(() => {
    preloadUISound();
  }, []);

  const playHoverSound = playUISound;

  const scrollToContent = () => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToParallax = () => {
    parallaxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Real-time player count from Firebase presence
  const playersOnline = usePlayersOnline();

  // Shuffle the 26-variant order once on mount; the order then stays stable
  // for the session, so left/right cycles deterministically and loops.
  const [carouselItems] = useState<CarouselItem[]>(() => shuffleArray(LANDING_PIECE_ICONS));
  const [carouselIndex, setCarouselIndex] = useState<number>(
    () => Math.floor(Math.random() * carouselItems.length)
  );
  const currentPiece = carouselItems[carouselIndex];
  const goPrevPiece = () =>
    setCarouselIndex(i => (i - 1 + carouselItems.length) % carouselItems.length);
  const goNextPiece = () =>
    setCarouselIndex(i => (i + 1) % carouselItems.length);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [randomSkyImage, setRandomSkyImage] = useState<string>(
    "/images/skytree.png"
  );
  const [animationDelay, setAnimationDelay] = useState<number>(0);
  const [moveDirection, setMoveDirection] = useState<string>("up");
  const [noRotateImage, setNoRotateImage] = useState<boolean>(false);
  const [showAboutModal, setShowAboutModal] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authModalMode, setAuthModalMode] = useState<'signIn' | 'signUp' | 'forgotPassword'>('signIn');
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showPremiumModal, setShowPremiumModal] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showDonationModal, setShowDonationModal] = useState<boolean>(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showFrog, setShowFrog] = useState<boolean>(false);
  const { user, userProfile, profileLoading, accountType, canPromote, isPremium, loginState } = useContext(AuthContext);
  const { t, language } = useLanguage();

  // Helper to get font size based on text length - scales gently as text gets longer
  const getHoverFontSize = (text: string): string => {
    const len = text.length;
    // Base size 9rem, start reducing at 7+ characters by 0.5rem per character
    const baseSize = 9;
    const reduction = Math.max(0, (len - 7) * 0.5);
    const fontSize = Math.max(4, baseSize - reduction); // Minimum 4rem
    return `${fontSize}rem`;
  };

  // Debug logging

  // Track scroll to show frog after parallax (roughly 2 viewport heights down)
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = window.innerHeight * 2; // After parallax
      setShowFrog(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handler for premium feature clicks
  const handlePremiumFeatureClick = (route: string) => {
    if (isPremium && accountType === 'real') {
      // User has premium, navigate to the page
      navigate(route);
    } else {
      // Show premium required modal
      setShowPremiumModal(true);
    }
  };

  // Handler to open auth modal for account creation
  const handleSignUpClick = () => {
    setAuthModalMode('signUp');
    setShowAuthModal(true);
  };

  // Listen for openAuthModal custom event
  useEffect(() => {
    const handleOpenAuthModal = (event: any) => {
      const mode = event.detail?.mode || 'signIn';
      setAuthModalMode(mode);
      setShowAuthModal(true);
    };

    document.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => {
      document.removeEventListener('openAuthModal', handleOpenAuthModal);
    };
  }, []);

  const getPieceImage = (zodiacResult: string) => {
    if (!zodiacResult) return '';
    const [variant, ...nameParts] = zodiacResult.split(' ');
    const pieceName = nameParts.join(' ');
    return `/Icons/${pieceName}-${variant.toLowerCase()}.png`;
  };

  const faqData = [
    {
      question: t('mainPage.faqQuestion1'),
      answer: t('mainPage.faqAnswer1')
    },
    {
      question: t('mainPage.faqQuestion2'),
      answer: t('mainPage.faqAnswer2')
    },
    {
      question: t('mainPage.faqQuestion3'),
      answer: t('mainPage.faqAnswer3')
    },
    {
      question: t('mainPage.faqQuestion4'),
      answer: t('mainPage.faqAnswer4')
    },
    {
      question: t('mainPage.faqQuestion5'),
      answer: t('mainPage.faqAnswer5')
    },
    {
      question: t('mainPage.faqQuestion6'),
      answer: t('mainPage.faqAnswer6')
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Username change handlers for real accounts
  const handleChangeUsername = async (newUsername: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || !userProfile) {
      return { success: false, error: 'User not authenticated' };
    }
    return await UserDataService.changeUsername(user.uid, newUsername, userProfile);
  };

  const handleCanChangeUsername = (): { canChange: boolean; reason?: string } => {
    if (!userProfile) {
      return { canChange: false, reason: 'Profile not loaded' };
    }

    // Check if user has changed username before
    if (userProfile.lastUsernameChange) {
      const daysSinceLastChange = Math.floor(
        (Date.now() - userProfile.lastUsernameChange.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastChange < 90) {
        const daysRemaining = 90 - daysSinceLastChange;
        return { canChange: false, reason: `You can change your username in ${daysRemaining} days` };
      }
    }

    return { canChange: true };
  };

  // List of available sky images
  const skyImages = [
    "/images/skybear.png",
    "/images/skyfrog.png",
    "/images/skygoat.png",
    "/images/skytree.png",
    "/images/skywolf.png",
    "/images/skycrane.png",
    "/images/skyrhino.png",
    "/images/skysnake.png",
    "/images/skyvirus.png",
    "/images/skymonkey.png",
    "/images/skyoctopus.png",
    "/images/skypenguin.png",
    "/images/skymushroom.png",
  ];

  // Images that should not be rotated
  const noRotateImages = [
    "/images/skywolf.png",
    "/images/skybear.png",
    "/images/skygoat.png",
    "/images/skymonkey.png",
    "/images/skyoctopus.png",
    "/images/skypenguin.png",
  ];

  // Select random image on component mount
  useEffect(() => {
    // Select random sky image
    const randomImageIndex = Math.floor(Math.random() * skyImages.length);
    const selectedImage = skyImages[randomImageIndex];
    setRandomSkyImage(selectedImage);

    // Check if it's an image that should not be rotated
    setNoRotateImage(noRotateImages.includes(selectedImage));

    // Randomly determine if the image should move up or down
    const direction = Math.random() > 0.5 ? "up" : "down";
    setMoveDirection(direction);

    // Generate a random starting position based on direction
    let randomStartPosition;
    if (direction === "up") {
      randomStartPosition = Math.floor(Math.random() * 75) * -1;
    } else {
      randomStartPosition = Math.floor(Math.random() * 75 + 75) * -1;
    }
    setAnimationDelay(randomStartPosition);
  }, []);

  // Function to get the appropriate CSS class for the image
  const getImageClass = () => {
    if (randomSkyImage === "/images/skyfrog.png") {
      return styles.learnMoreTransparentImageFrog;
    } else if (randomSkyImage === "/images/skypenguin.png") {
      return styles.learnMoreTransparentImagePenguin;
    } else if (randomSkyImage === "/images/skymonkey.png") {
      return styles.learnMoreTransparentImageMonkey;
    } else if (noRotateImages.includes(randomSkyImage)) {
      return styles.learnMoreTransparentImageNoRotate;
    } else {
      return styles.learnMoreTransparentImage;
    }
  };

  const sections = [
    { id: "what-is-zodiac", title: "What is?", icon: "/Icons/light1.png" },
    { id: "setup", title: "Setup", icon: "/Icons/abilities.png" },
    { id: "basic-rules", title: "Rules", icon: "/Icons/rules.png" },
    {
      id: PieceType.Bear,
      title: "Bear",
      icon: "/Icons/Bear-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Crane,
      title: "Crane",
      icon: "/Icons/Crane-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Frog,
      title: "Frog",
      icon: "/Icons/Frog-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Fungi,
      title: "Fungi",
      icon: "/Icons/Fungi-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Goat,
      title: "Goat",
      icon: "/Icons/Goat-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Monkey,
      title: "Monkey",
      icon: "/Icons/Monkey-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Octopus,
      title: "Octopus",
      icon: "/Icons/Octopus-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Penguin,
      title: "Penguin",
      icon: "/Icons/Penguin-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Rhino,
      title: "Rhino",
      icon: "/Icons/Rhino-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Snake,
      title: "Snake",
      icon: "/Icons/Snake-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Tree,
      title: "Tree",
      icon: "/Icons/Tree-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Wolf,
      title: "Wolf",
      icon: "/Icons/Wolf-light.png",
      isPiece: true,
    },
    {
      id: PieceType.Virus,
      title: "Virus",
      icon: "/Icons/Virus-light.png",
      isPiece: true,
    },
  ];

  return (
    <AdSenseLayout hideUntilScroll>
      <div className={styles.container}>
        {/* Players Online - Fixed at very top */}
        <div className={styles.playersOnlineBar}>
          <p className={styles.playersOnline}>{t('mainPage.playersOnline')}: {playersOnline}</p>
        </div>
        <GlobalHeader className={styles.mainPageHeader} />

        {/* Landing Section - Above Parallax */}
        <div className={styles.landingSection}>
          {/* Menu on the left */}
          <div className={styles.landingMenu}>
            <div className={styles.compactGameOptions}>
              <Link to="/challenge-ai" className={styles.compactOptionCard} onMouseEnter={playHoverSound}>
                <span>{t('mainPage.challengeAI')}</span>
              </Link>
              <Link to="/multiplayer" className={styles.compactOptionCard} onMouseEnter={playHoverSound}>
                <span>{t('mainPage.multiplayer')}</span>
              </Link>
              <Link to="/sandbox" className={styles.compactOptionCard} onMouseEnter={playHoverSound}>
                <span>{t('mainPage.sandbox')}</span>
              </Link>
              <Link to="/puzzles" className={styles.compactOptionCard} onMouseEnter={playHoverSound}>
                <span>Puzzles</span>
              </Link>
              <Link to="/tutorials" className={styles.compactOptionCard} onMouseEnter={playHoverSound}>
                <span>{t('mainPage.tutorials')}</span>
              </Link>
              <button
                onClick={() => handlePremiumFeatureClick('/analytics')}
                className={styles.compactOptionCard}
                onMouseEnter={playHoverSound}
              >
                <span>{t('mainPage.analytics')}</span>
              </button>
              <button
                onClick={() => {
                  if (accountType === 'real') {
                    navigate('/history');
                  } else {
                    setAuthModalMode('signUp');
                    setShowAuthModal(true);
                  }
                }}
                className={styles.compactOptionCard}
                onMouseEnter={playHoverSound}
              >
                <span>Game History</span>
              </button>
              {!isPremium && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className={styles.compactOptionCard}
                  onMouseEnter={playHoverSound}
                >
                  <span>{t('mainPage.upgradeAccount')}</span>
                </button>
              )}
            </div>

            {/* Profile Section */}
            {(accountType === 'temp' || accountType === 'real') && userProfile && !profileLoading && (
              <button
                className={styles.profileSection}
                onClick={() => setShowProfileModal(true)}
                onMouseEnter={playHoverSound}
                title={accountType === 'temp' ? "Temporary Account - Click to view profile" : "View Profile"}
              >
                <div className={styles.profileAvatar}>
                  {userProfile.zodiacResult ? (
                    <img
                      src={getPieceImage(userProfile.zodiacResult)}
                      alt="Your zodiac piece"
                      className={styles.zodiacAvatar}
                    />
                  ) : (
                    <div className={styles.avatarLetter}>
                      {userProfile.username ? userProfile.username.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                </div>
                <div className={styles.profileInfo}>
                  <div className={styles.profileUsername}>
                    {userProfile.username || 'Anonymous'}
                    {accountType === 'temp' && <span className={styles.tempBadge}>TEMP</span>}
                  </div>
                  <div className={styles.profileElo}>
                    {(() => {
                      const highest = MMRService.getHighestRating(userProfile);
                      return highest.isPlaced
                        ? `${highest.rating} ELO`
                        : `${t('mainPage.placementProgress')} ${userProfile.mmr.placementGames}/10`;
                    })()}
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Centered Logo and Title */}
          <div className={styles.landingCenter}>
            {/* Logo with text underneath - like parallax */}
            <div className={styles.landingLogoWrapper}>
              <span className={styles.landingLogoText}>ZODIAC</span>
              <h1 className={styles.landingTitle}>ANIMAL CHESS</h1>
              <img
                src="/Icons/logo/Zodiaclogo.png"
                alt="Zodiac Logo"
                className={styles.landingLogoIcon}
              />
            </div>
            <p className={styles.landingSubtitle}>A GAME FOR HUMANS</p>
            <div className={styles.landingCarousel}>
              <button
                type="button"
                aria-label="Previous piece"
                className={`${styles.landingCarouselArrow} ${styles.landingCarouselArrowLeft}`}
                onClick={() => { goPrevPiece(); playHoverSound(); }}
              >
                <svg viewBox="0 0 24 40" aria-hidden="true" focusable="false">
                  <path d="M18 4 L6 20 L18 36" />
                </svg>
                <span className={styles.landingCarouselArrowGlyph} />
              </button>
              <img
                key={currentPiece.file}
                src={`/Icons/${currentPiece.file}`}
                alt={currentPiece.name}
                className={`${styles.landingIcon} ${currentPiece.variant === 'light' ? styles.lightIcon : ''} ${currentPiece.name === 'Penguin' ? styles.penguin : ''}`}
              />
              <button
                type="button"
                aria-label="Next piece"
                className={`${styles.landingCarouselArrow} ${styles.landingCarouselArrowRight}`}
                onClick={() => { goNextPiece(); playHoverSound(); }}
              >
                <svg viewBox="0 0 24 40" aria-hidden="true" focusable="false">
                  <path d="M6 4 L18 20 L6 36" />
                </svg>
                <span className={styles.landingCarouselArrowGlyph} />
              </button>
            </div>
            <div className={styles.landingPieceVibe}>
              {t(`pieceVibes.${currentPiece.name}.${currentPiece.variant}`)}
            </div>
            <button
              type="button"
              className={styles.landingPlayNow}
              onClick={() => {
                playUISound();  // same tick as the menu cards — pressing it should feel like them
                // PLAY NOW MEANS PLAY NOW: it hands ChallengeAI a quickStart every time, and
                // that screen deals a random beginner game off it (weak AI, no king picking).
                // The settings panel is right there for anyone who wants a rostered game.
                //
                // The welcome card rides along on the FIRST press only — the flag is written
                // once it has been shown. ?welcome=1 forces it back for retesting the copy.
                const forceWelcome = new URLSearchParams(window.location.search).get('welcome') === '1';
                const seenWelcome = !forceWelcome && localStorage.getItem('zodiacWelcomeSeen') === 'true';
                navigate('/challenge-ai', { state: { quickStart: true, newPlayerWelcome: !seenWelcome } });
              }}
            >
              PLAY NOW
            </button>
          </div>

          {/* Scroll indicator */}
          <div className={styles.landingScrollIndicator} onClick={scrollToParallax}>
            <div className={styles.landingScrollArrow}>▼</div>
          </div>
        </div>

        {/* Firewatch-style Parallax Hero */}
        <div ref={parallaxRef}>
          <ParallaxHero onScrollToContent={scrollToContent} />
        </div>

        {/* Main Content */}
        <div ref={contentRef} className={styles.contentSection}>
        </div>
        <div className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.learnMoreSection}>
            <h2 className={styles.learnMoreTitle}>{t('mainPage.learnThePieces')}</h2>
            <div className={styles.learnMoreContainer}>
              {/* Large transparent background image */}
              <div className={styles.learnMoreLargeBackgroundImage}>
                <img
                  src={randomSkyImage}
                  alt=""
                  className={getImageClass()}
                  style={{
                    top: `${animationDelay}%`,
                    animationPlayState: "running",
                    animationDirection:
                      moveDirection === "up" ? "normal" : "reverse",
                  }}
                />
              </div>

              {!hoveredIcon ? (
                <div
                  className={`${styles.learnMoreCenterText} ${styles.learnMoreDefaultCenterText}`}
                ></div>
              ) : (
                <div
                  className={styles.learnMoreCenterText}
                  style={{ fontSize: getHoverFontSize(hoveredIcon) }}
                >
                  {hoveredIcon}
                </div>
              )}

              <div className={styles.learnMoreContent}>
                <div className={styles.learnMoreIconsContainer}>
                  <div className={styles.learnMoreDiamondGrid}>
                    {/* Top row - 4 pieces */}
                    <div className={styles.learnMoreRow}>
                      {sections
                        .filter((s) => s.isPiece)
                        .slice(0, 4)
                        .map((piece) => (
                          <Link
                            key={piece.id}
                            to={`/piecerules?piece=${piece.id}`}
                            className={styles.learnMorePieceCard}
                            onMouseEnter={() => {
                              setHoveredIcon(t(`pieces.${piece.title}`).toUpperCase());
                              playHoverSound();
                            }}
                            onMouseLeave={() => setHoveredIcon(null)}
                          >
                            <img
                              src={piece.icon}
                              alt={piece.title}
                              className={styles.learnMorePieceCardIcon}
                            />
                          </Link>
                        ))}
                    </div>

                    {/* Middle row - 5 pieces */}
                    <div className={styles.learnMoreRow}>
                      {sections
                        .filter((s) => s.isPiece)
                        .slice(4, 9)
                        .map((piece) => (
                          <Link
                            key={piece.id}
                            to={`/piecerules?piece=${piece.id}`}
                            className={styles.learnMorePieceCard}
                            onMouseEnter={() => {
                              setHoveredIcon(t(`pieces.${piece.title}`).toUpperCase());
                              playHoverSound();
                            }}
                            onMouseLeave={() => setHoveredIcon(null)}
                          >
                            <img
                              src={piece.icon}
                              alt={piece.title}
                              className={styles.learnMorePieceCardIcon}
                            />
                          </Link>
                        ))}
                    </div>

                    {/* Bottom row - 4 pieces */}
                    <div className={styles.learnMoreRow}>
                      {sections
                        .filter((s) => s.isPiece)
                        .slice(9, 13)
                        .map((piece) => (
                          <Link
                            key={piece.id}
                            to={`/piecerules?piece=${piece.id}`}
                            className={styles.learnMorePieceCard}
                            onMouseEnter={() => {
                              setHoveredIcon(t(`pieces.${piece.title}`).toUpperCase());
                              playHoverSound();
                            }}
                            onMouseLeave={() => setHoveredIcon(null)}
                          >
                            <img
                              src={piece.icon}
                              alt={piece.title}
                              className={styles.learnMorePieceCardIcon}
                            />
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll indicator */}
            <div className={styles.learnMoreScrollIndicator}>
              <div className={styles.learnMoreScrollArrow}>▼</div>
            </div>
          </div>

          <div className={styles.learnRulesSection}>
            <h2 className={styles.learnRulesTitle}>{t('mainPage.learnTheRules')}</h2>
            <div className={styles.learnRulesContainer}>
              <div className={styles.rulesGrid}>
                <div className={styles.ruleCard}>
                  <h3>{t('mainPage.premiseTitle')}</h3>
                  <p>{t('mainPage.premiseText')}</p>
                </div>

                <div className={styles.ruleCard}>
                  <h3>{t('mainPage.selectionTitle')}</h3>
                  <p>{t('mainPage.selectionText')}</p>
                </div>

                <div className={styles.ruleCard}>
                  <h3>{t('mainPage.objectiveTitle')}</h3>
                  <p>{t('mainPage.objectiveText')}</p>
                </div>

                <div className={styles.ruleCard}>
                  <h3>{t('mainPage.competeTitle')}</h3>
                  <p>{t('mainPage.competeText')}</p>
                </div>
              </div>

              <div className={styles.fullRulesLink}>
                <a
                  href="/Comprehensive Rule book.pdf"
                  target="_blank"
                  className={styles.officialRulesButton}
                >
                  {t('mainPage.fullRulesButton')}
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className={styles.faqSection}>
            <h2 className={styles.faqTitle}>{t('mainPage.faqTitle')}</h2>
            <div className={styles.faqContainer}>
              {faqData.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === index}
                  onToggle={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.devAccess} ${!showFrog ? styles.devAccessHidden : ''}`}>
        <button
          onClick={() => setShowAboutModal(true)}
          className={styles.devLink}
        >
          {t('mainPage.aboutButton')}
        </button>
        <Link to="/privacy" className={styles.devLink}>
          {t('mainPage.privacyPolicy')}
        </Link>
        <Link to="/game" className={styles.devLink}>
          {t('mainPage.developerInterface')}
        </Link>
      </div>

      <AboutModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
      />

      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userData={null}
        changeUsername={handleChangeUsername}
        canChangeUsername={handleCanChangeUsername}
      />

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        accountType={accountType}
        userEmail={user?.email}
        userId={user?.uid}
        onSignUpClick={handleSignUpClick}
      />

      <DonationModal
        isOpen={showDonationModal}
        onClose={() => setShowDonationModal(false)}
      />

        {/* Floating Frog Donation Button - appears after scrolling past parallax */}
        <button
          className={`${styles.floatingFrog} ${!showFrog ? styles.floatingFrogHidden : ''}`}
          onClick={() => setShowDonationModal(true)}
          title={t('donation.supportDevelopment')}
        >
          <img src="/images/Frognate2.png" alt="Donate" />
        </button>

      </div>
    </AdSenseLayout>
  );
}

export default MainPage;
